import React, { useState, useEffect, useRef } from 'react';
import { poiService, POI, Card } from '../services/poiService';
import { useLanguage } from '../contexts/LanguageContext';
import RichTextDisplay from './RichTextDisplay';
import { getReadableCategoryName } from '../utils/categoryUtils';

interface POICarouselProps {
  onPOIClick?: (poi: POI) => void;
}

interface POIWithCard {
  poi: POI;
  card: Card | null;
  isLoadingCard: boolean;
}

const POICarousel: React.FC<POICarouselProps> = ({ onPOIClick }) => {
  const { selectedLanguage } = useLanguage();
  const [poisWithCards, setPoisWithCards] = useState<POIWithCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoadingPOIs, setIsLoadingPOIs] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(true);

  // Function to fetch and append random POIs
  const fetchAndAppendRandomPOIs = async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setIsLoadingPOIs(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      
      const response = await poiService.fetchRandomPOIs(10); // Fetch 10 random POIs
      
      // Initialize POIs with null cards
      const newPoisWithCards: POIWithCard[] = response.data.map(poi => ({
        poi,
        card: null,
        isLoadingCard: true
      }));
      
      setPoisWithCards(prev => isInitial ? newPoisWithCards : [...prev, ...newPoisWithCards]);
      
      // Fetch first card for each POI
      const startIndex = isInitial ? 0 : poisWithCards.length;
      response.data.forEach(async (poi, index) => {
        try {
          const cardsResponse = await poiService.fetchCardsByPOI(poi.id, selectedLanguage?.code);
          const firstCard = cardsResponse.data.length > 0 ? cardsResponse.data[0] : null;
          
          setPoisWithCards(prev => {
            const updated = [...prev];
            const absoluteIndex = startIndex + index;
            if (absoluteIndex < updated.length) {
              updated[absoluteIndex] = {
                ...updated[absoluteIndex],
                card: firstCard,
                isLoadingCard: false
              };
            }
            return updated;
          });
        } catch (err) {
          console.error(`Error fetching cards for POI ${poi.id}:`, err);
          setPoisWithCards(prev => {
            const updated = [...prev];
            const absoluteIndex = startIndex + index;
            if (absoluteIndex < updated.length) {
              updated[absoluteIndex] = {
                ...updated[absoluteIndex],
                card: null,
                isLoadingCard: false
              };
            }
            return updated;
          });
        }
      });
    } catch (err) {
      console.error('Error fetching random POIs:', err);
      if (isInitial) {
        setError('Failed to load POI carousel');
      }
    } finally {
      if (isInitial) {
        setIsLoadingPOIs(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  // Fetch random POIs on mount
  useEffect(() => {
    fetchAndAppendRandomPOIs(true);
  }, [selectedLanguage]);

  // Handle touch events for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextCard();
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Navigate to next card and load more if needed
  const nextCard = () => {
    if (currentIndex < poisWithCards.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      
      // Load more POIs when approaching the end (2 cards before the end)
      if (newIndex >= poisWithCards.length - 2 && !isLoadingMore && hasMoreRef.current) {
        fetchAndAppendRandomPOIs(false);
      }
    }
  };

  // Navigate to previous card
  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Auto-scroll the carousel container
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.clientWidth;
      container.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentIndex]);

  if (isLoadingPOIs) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6 sm:mb-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 dark:border-primary-400 mr-3"></div>
          <span className="text-gray-600 dark:text-gray-400 dark:text-gray-500">Loading POI carousel...</span>
        </div>
      </div>
    );
  }

  if (error || poisWithCards.length === 0) {
    return null; // Don't show carousel if there's an error or no POIs
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
          Discover Random Places
        </h2>
      </div>

      <div className="relative">
        {/* Navigation Arrows */}
        {poisWithCards.length > 1 && (
          <>
            <button
              onClick={prevCard}
              disabled={currentIndex === 0}
              className={`hidden sm:flex absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-12 p-3 rounded-full shadow-lg transition-all z-20 ${
                currentIndex === 0 
                  ? 'bg-gray-300 text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 hover:shadow-xl'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={nextCard}
              disabled={isLoadingMore}
              className={`hidden sm:flex absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-12 p-3 rounded-full shadow-lg transition-all z-20 ${
                isLoadingMore 
                  ? 'bg-gray-300 text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 hover:shadow-xl'
              }`}
            >
              {isLoadingMore ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </>
        )}

        {/* Carousel Container */}
        <div 
          className="relative overflow-hidden rounded-lg"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            ref={scrollContainerRef}
            className="flex transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {poisWithCards.map((poiWithCard) => (
              <div
                key={poiWithCard.poi.id}
                className="w-full flex-shrink-0"
              >
                <POICarouselItem 
                  poiWithCard={poiWithCard}
                  onPOIClick={onPOIClick}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual POI Carousel Item Component
const POICarouselItem: React.FC<{
  poiWithCard: POIWithCard;
  onPOIClick?: (poi: POI) => void;
}> = ({ poiWithCard, onPOIClick }) => {
  const { poi, card, isLoadingCard } = poiWithCard;

  const handleClick = () => {
    onPOIClick?.(poi);
  };

  return (
    <div 
      className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleClick}
    >
      {/* POI Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-4">
        <h3 className="text-xl font-semibold mb-2">{poi.name}</h3>
        <div className="flex items-center text-sm opacity-90">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span>{poi.city || 'Unknown City'}, {poi.country || 'Unknown Country'}</span>
        </div>
        {poi.category && (
          <div className="mt-2">
            <span className="inline-block bg-white dark:bg-gray-800 bg-opacity-20 text-white text-xs px-2 py-1 rounded-full">
              {getReadableCategoryName(poi.category)}
            </span>
          </div>
        )}
      </div>

      {/* Card Content */}
      {isLoadingCard ? (
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 dark:border-primary-400 mr-2"></div>
          <span className="text-gray-600 dark:text-gray-400 dark:text-gray-500 text-sm">Loading card...</span>
        </div>
      ) : card ? (
        <div className="p-4">
          {/* Card Image */}
          {card.image_url && (
            <div className="mb-3">
              <img 
                src={card.image_url} 
                alt="Card image"
                className="w-full h-48 object-cover rounded-lg"
                loading="lazy"
                crossOrigin="anonymous"
                referrerPolicy="strict-origin-when-cross-origin"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* Card Text */}
          {card.text && (
            <div className="mb-3">
              <RichTextDisplay 
                content={card.text} 
                className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm"
                maxLines={3}
                enableShowMore={false}
              />
            </div>
          )}

          {/* Card Category Badge */}
          {card.category && (
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              <span>{getReadableCategoryName(card.category)}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400 dark:text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm">No cards available for this location</p>
        </div>
      )}

      {/* Click to view more CTA */}
      <div className="px-4 pb-4">
        <div className="text-center py-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors">
          Click to view all cards
        </div>
      </div>
    </div>
  );
};

export default POICarousel;
